import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  Typography,
  Checkbox,
  Alert,
  Snackbar,
  TextField,
  List,
  ListItem,
  IconButton,
  useTheme,
  useMediaQuery,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import { StrictModeDroppable } from './Droppable';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { v4 as uuidv4 } from 'uuid';
// import { SendToThings } from './SendToThings';
import supabase from '../supabaseClient';
import UserContext from '../contexts/userContext';

// Helper function to check if user can modify a list
const canUserModifyList = async (userId, listId) => {
  try {
    // Get the list details
    const { data: listData, error: listError } = await supabase
      .from('saved_lists')
      .select('user_id, shared_with_household')
      .eq('list_id', listId)
      .single();

    if (listError || !listData) {
      return false;
    }

    // If user is the owner, they can modify
    if (listData.user_id === userId) {
      return true;
    }

    // If list is not shared with household, only owner can modify
    if (!listData.shared_with_household) {
      return false;
    }

    // Check if both users are in the same household
    const { data: userHousehold } = await supabase
      .from('households_users')
      .select('household_id')
      .eq('user_id', userId)
      .single();

    if (!userHousehold) {
      return false;
    }

    const { data: ownerHousehold } = await supabase
      .from('households_users')
      .select('household_id')
      .eq('user_id', listData.user_id)
      .single();

    if (!ownerHousehold) {
      return false;
    }

    // Check if they're in the same household
    return userHousehold.household_id === ownerHousehold.household_id;
  } catch (error) {
    console.error('Error checking list permissions:', error);
    return false;
  }
};

export function ShoppingList({ listId, onRegenerate, isRegenerating }) {
  const { user } = useContext(UserContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [listItems, setListItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [newItemValue, setNewItemValue] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [deletedItem, setDeletedItem] = useState({ item: null, index: -1 });
  const [inputFieldInitialPosition, setInputFieldInitialPosition] = useState(null);
  const [showFixedInput, setShowFixedInput] = useState(false);
  const [newItemId, setNewItemId] = useState(null);
  const [canModify, setCanModify] = useState(false);
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(true);

  const editingRef = useRef(null);
  const newItemRef = useRef(null);
  const caretPositionRef = useRef(null);
  const startOfListRef = useRef(null);
  const itemRefs = useRef({});
  const permissionCheckRef = useRef(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (editingRef.current) {
      editingRef.current.focus();
      const input = editingRef.current;
      if (caretPositionRef.current !== null && input.setSelectionRange) {
        const position = caretPositionRef.current;
        input.setSelectionRange(position, position);
      }
    }
  }, [editingId]);

  const handleEdit = (e, id) => {
    const item = listItems.find((item) => item.id === id);
    setEditingId(id);
    setEditingValue(item.name);

    // Get the click position
    const clickX = e.clientX;
    const clickY = e.clientY;

    // Calculate caret position
    let caretPosition = 0;

    // Get the caret position within the element
    if (document.caretPositionFromPoint) {
      // Firefox
      const position = document.caretPositionFromPoint(clickX, clickY);
      if (position) {
        caretPosition = position.offset;
      }
    } else if (document.caretRangeFromPoint) {
      // Chrome, Safari
      const range = document.caretRangeFromPoint(clickX, clickY);
      if (range) {
        caretPosition = range.startOffset;
      }
    }

    caretPositionRef.current = caretPosition;
  };

  const handleUpdate = async (id) => {
    if (!canModify) {
      console.warn('User does not have permission to modify this list');
      return;
    }

    const updatedItems = listItems.map((item) =>
      item.id === id ? { ...item, name: editingValue } : item
    );
    setListItems(updatedItems);
    setEditingId(null);
    setEditingValue('');
    caretPositionRef.current = null;

    try {
      const { error } = await supabase
        .from('lists_shopping_list')
        .update({ ingredient_json: updatedItems })
        .eq('list_id', listId);
      if (error) throw error;
    } catch (error) {
      console.error('Error updating item:', error);
    }
  };

  const handleCheckboxChange = async (id) => {
    if (!canModify) {
      console.warn('User does not have permission to modify this list');
      return;
    }

    const updatedItems = listItems.map((item) =>
      item.id === id ? { ...item, checkbox: !item.checkbox } : item
    );
    setListItems(updatedItems);

    try {
      const { error } = await supabase
        .from('lists_shopping_list')
        .update({ ingredient_json: updatedItems })
        .eq('list_id', listId);
      if (error) throw error;
    } catch (error) {
      console.error('Error updating checkbox:', error);
    }
  };

  useEffect(() => {
    if (!listId || !user) return;

    // Create a unique identifier for this permission check
    const checkId = `${listId}-${user.id}`;
    permissionCheckRef.current = checkId;

    // Reset permission checking state synchronously
    setIsCheckingPermissions(true);
    setCanModify(false);

    // Check permissions first
    const checkPermissions = async () => {
      const hasPermission = await canUserModifyList(user.id, listId);
      
      // Only update state if this is still the current permission check
      if (permissionCheckRef.current === checkId) {
        setCanModify(hasPermission);
        setIsCheckingPermissions(false);
      }
    };

    checkPermissions();

    // Fetch initial items
    const fetchItems = async () => {
      const { data, error } = await supabase
        .from('lists_shopping_list')
        .select('ingredient_json')
        .eq('list_id', listId);

      if (error) {
        console.error('Error fetching items:', error);
        return;
      }

      if (data && data[0]?.ingredient_json) {
        setListItems(data[0].ingredient_json);
      }
    };
    fetchItems();

    // Subscribe to realtime updates
    const channel = supabase
      .channel(`public:lists_shopping_list:list_id=eq.${listId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'lists_shopping_list', filter: `list_id=eq.${listId}` },
        (payload) => {
          if (payload.new && payload.new.ingredient_json) {
            setListItems(payload.new.ingredient_json);
          }
        }
      )
      .subscribe();

    // Cleanup subscription on unmount and mark permission check as stale
    return () => {
      permissionCheckRef.current = null;
      supabase.removeChannel(channel);
    };
  }, [listId, user]);

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    if (!canModify) {
      console.warn('User does not have permission to modify this list');
      return;
    }

    const reorderedItems = [...listItems];
    const [removed] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, removed);

    setListItems(reorderedItems);

    try {
      const { error } = await supabase
        .from('lists_shopping_list')
        .update({ ingredient_json: reorderedItems })
        .eq('list_id', listId);
      if (error) throw error;
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const deleteItem = async (id) => {
    if (!canModify) {
      console.warn('User does not have permission to modify this list');
      return;
    }

    const indexToDelete = listItems.findIndex((item) => item.id === id);
    const itemToDelete = listItems[indexToDelete];

    const updatedItems = listItems.filter((item) => item.id !== id);
    setListItems(updatedItems);
    setSnackbarOpen(true);

    setDeletedItem({ item: itemToDelete, index: indexToDelete });

    try {
      const { error } = await supabase
        .from('lists_shopping_list')
        .update({ ingredient_json: updatedItems })
        .eq('list_id', listId);
      if (error) throw error;
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const addNewItem = async (itemName = '') => {
    if (!itemName.trim()) return null;
    if (!canModify) {
      console.warn('User does not have permission to modify this list');
      return null;
    }

    const newItem = {
      id: uuidv4(),
      name: itemName.trim(),
      checkbox: false,
    };

    const updatedItems = [...listItems, newItem];
    setListItems(updatedItems);
    setNewItemId(newItem.id); // Store the new item's ID

    try {
      const { error } = await supabase
        .from('lists_shopping_list')
        .update({ ingredient_json: updatedItems })
        .eq('list_id', listId);
      if (error) throw error;
    } catch (error) {
      console.error('Error adding new item:', error);
    }

    return newItem.id;
  };

  const undoDelete = async () => {
    if (deletedItem.item && deletedItem.index > -1) {
      const restoredList = [...listItems];
      restoredList.splice(deletedItem.index, 0, deletedItem.item);
      setListItems(restoredList);

      try {
        const { error } = await supabase
          .from('lists_shopping_list')
          .update({ ingredient_json: restoredList })
          .eq('list_id', listId);
        if (error) throw error;
      } catch (error) {
        console.error('Error restoring item:', error);
      }
    }

    closeSnackbar();
  };

  const closeSnackbar = () => {
    setSnackbarOpen(false);
    setDeletedItem(null);
  };

  const handleInputFocus = () => {
    if (newItemRef.current) {
      const rect = newItemRef.current.getBoundingClientRect();
      setInputFieldInitialPosition({ top: rect.top });
    }
  };

  const handleInputBlur = () => {
    setInputFieldInitialPosition(null);
  };

  useEffect(() => {
    if (inputFieldInitialPosition && newItemRef.current) {
      const rect = newItemRef.current.getBoundingClientRect();
      const deltaY = rect.top - inputFieldInitialPosition.top;
      window.scrollBy(0, deltaY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listItems.length]);

  // Scroll to bottom when a new item is added
  useEffect(() => {
    if (newItemId && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
      setNewItemId(null);
    }
  }, [listItems, newItemId]);

  // Show fixed input immediately on mobile
  useEffect(() => {
    if (isMobile) {
      setShowFixedInput(true);
    } else {
      setShowFixedInput(false);
    }
  }, [isMobile]);

  const isDark = theme.palette.mode === 'dark';
  const accent = isDark ? '#BB86FC' : '#6366f1';

  // Count checked vs unchecked - expose via callback if needed
  const checkedCount = listItems.filter(item => item.checkbox).length;
  const totalCount = listItems.length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Reference point to determine scroll position */}
      <div ref={startOfListRef}></div>

      {/* Progress indicator - compact inline version */}
      {totalCount > 0 && (
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                height: 3,
                borderRadius: 2,
                backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${(checkedCount / totalCount) * 100}%`,
                  background: `linear-gradient(90deg, ${accent}, ${isDark ? '#CF6FFF' : '#818cf8'})`,
                  borderRadius: 2,
                  transition: 'width 0.3s ease',
                }}
              />
            </Box>
          </Box>
          <Typography
            sx={{
              fontSize: '0.7rem',
              color: checkedCount === totalCount ? accent : theme.palette.text.secondary,
              fontFamily: theme.typography.fontFamily,
              fontWeight: checkedCount === totalCount ? 600 : 400,
              whiteSpace: 'nowrap',
            }}
          >
            {checkedCount === totalCount ? '✓ done' : `${checkedCount}/${totalCount}`}
          </Typography>
        </Box>
      )}

      {/* Scrollable list area */}
      <Box ref={scrollContainerRef} sx={{ flex: 1, overflowY: 'auto', minHeight: 0, mr: -0.5, pr: 0.5 }}>
        <DragDropContext onDragEnd={onDragEnd}>
        <StrictModeDroppable droppableId="droppable">
          {(provided) => (
            <List
              sx={{
                padding: 0,
                ...(isMobile ? { paddingBottom: '80px' } : {}),
              }}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {listItems.map((item, index) => (
                <Draggable 
                  key={item.id} 
                  draggableId={item.id} 
                  index={index}
                  isDragDisabled={!canModify || isCheckingPermissions}
                >
                  {(provided, snapshot) => (
                    <Box
                      ref={(el) => {
                        provided.innerRef(el);
                        if (el) {
                          itemRefs.current[item.id] = el;
                        } else {
                          delete itemRefs.current[item.id];
                        }
                      }}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      sx={{
                        mb: 0.5,
                      }}
                    >
                      <ListItem
                        sx={{
                          padding: '6px 8px',
                          borderRadius: '10px',
                          backgroundColor: snapshot.isDragging
                            ? isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'
                            : item.checkbox
                              ? isDark ? 'rgba(255,255,255,0.015)' : 'rgba(0,0,0,0.01)'
                              : isDark ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.02)',
                          border: `1px solid ${
                            snapshot.isDragging
                              ? accent + '40'
                              : isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'
                          }`,
                          boxShadow: snapshot.isDragging 
                            ? `0 8px 24px -8px ${accent}30`
                            : 'none',
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            backgroundColor: item.checkbox
                              ? isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.015)'
                              : isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                            borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                            '& .delete-btn': {
                              opacity: 0.5,
                            },
                          },
                        }}
                        onClick={(e) => handleEdit(e, item.id)}
                      >
                        <Checkbox
                          checked={item.checkbox}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => {
                            handleCheckboxChange(item.id);
                          }}
                          disabled={!canModify || isCheckingPermissions}
                          size="small"
                          sx={{
                            alignSelf: 'center',
                            padding: '6px',
                            ml: -0.5,
                            mr: 0.5,
                            color: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)',
                            transition: 'color 0.15s ease',
                            '&.Mui-checked': {
                              color: accent,
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: 20,
                            },
                          }}
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            gap: 0.5,
                          }}
                        >
                          {editingId === item.id ? (
                            <TextField
                              multiline
                              variant="standard"
                              sx={{
                                flex: 1,
                                '& .MuiInputBase-input': {
                                  padding: 0,
                                  fontSize: '0.85rem',
                                  lineHeight: 1.4,
                                },
                                '& .MuiInput-underline:before': {
                                  borderBottom: 'none',
                                },
                                '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                                  borderBottom: 'none',
                                },
                                fontFamily: theme.typography.fontFamily,
                              }}
                              InputProps={{
                                inputRef: editingRef,
                              }}
                              fullWidth
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleUpdate(item.id);
                                }
                              }}
                              autoFocus
                              onBlur={() => {
                                handleUpdate(item.id);
                              }}
                            />
                          ) : (
                            <Typography
                              onClick={(e) => canModify && handleEdit(e, item.id)}
                              sx={{
                                cursor: canModify ? 'pointer' : 'default',
                                flex: 1,
                                whiteSpace: 'pre-wrap',
                                fontFamily: theme.typography.fontFamily,
                                fontSize: '0.85rem',
                                lineHeight: 1.4,
                                color: item.checkbox 
                                  ? theme.palette.text.disabled 
                                  : theme.palette.text.primary,
                                textDecoration: item.checkbox ? 'line-through' : 'none',
                                textDecorationColor: item.checkbox ? accent + '50' : 'transparent',
                                transition: 'all 0.15s ease',
                              }}
                            >
                              {item.name}
                            </Typography>
                          )}
                          <IconButton
                            className="delete-btn"
                            sx={{
                              flexShrink: 0,
                              padding: '8px',
                              margin: '-4px',
                              marginRight: '-6px',
                              opacity: 0.2,
                              transition: 'all 0.15s ease',
                              borderRadius: '8px',
                              '&:hover': {
                                opacity: 1,
                                color: theme.palette.error.main,
                                backgroundColor: `${theme.palette.error.main}10`,
                              },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (canModify) {
                                deleteItem(item.id);
                              }
                            }}
                            disabled={!canModify}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      </ListItem>
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </List>
          )}
        </StrictModeDroppable>
      </DragDropContext>
      </Box>

      {/* Conditionally render the input field based on device */}
      {isMobile ? (
          editingId === null ? (
            // Mobile version: Fixed input field at the bottom
            <Box
              sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: isDark 
                  ? 'rgba(18, 18, 18, 0.95)' 
                  : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(12px)',
                padding: '12px 16px',
                borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                zIndex: 1000,
                display: showFixedInput ? 'flex' : 'none',
                justifyContent: 'center',
              }}
            >
              <Box sx={{ maxWidth: '400px', width: '100%' }}>
                <TextField
                  value={newItemValue}
                  onChange={(e) => setNewItemValue(e.target.value)}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  onKeyPress={async (e) => {
                    if (e.key === 'Enter' && canModify) {
                      await addNewItem(newItemValue);
                      setNewItemValue('');
                      if (newItemRef.current) {
                        newItemRef.current.focus();
                      }
                    }
                  }}
                  placeholder={canModify ? "add item..." : "read-only"}
                  variant="outlined"
                  fullWidth
                  size="small"
                  disabled={!canModify || isCheckingPermissions}
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      padding: '10px 12px',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      opacity: 0.5,
                    },
                  }}
                  inputRef={newItemRef}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={async () => {
                          if (canModify) {
                            await addNewItem(newItemValue);
                            setNewItemValue('');
                            if (newItemRef.current) {
                              newItemRef.current.focus();
                            }
                          }
                        }}
                        edge="end"
                        size="small"
                        disabled={!newItemValue.trim() || !canModify || isCheckingPermissions}
                        sx={{
                          color: newItemValue.trim() ? accent : 'inherit',
                          opacity: newItemValue.trim() ? 1 : 0.4,
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    ),
                  }}
                />
              </Box>
            </Box>
          ) : null
        ) : (
          // Desktop version: Sticky bottom input + regenerate
          <Box 
            sx={{ 
              mt: 'auto',
              pt: 1.5,
              flexShrink: 0,
            }}
          >
            {/* Add item input */}
            <Box 
              sx={{ 
                pt: 1,
                borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <AddIcon 
                sx={{ 
                  fontSize: 16, 
                  color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.25)',
                  flexShrink: 0,
                }} 
              />
              <TextField
                value={newItemValue}
                onChange={(e) => setNewItemValue(e.target.value)}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onKeyPress={async (e) => {
                  if (e.key === 'Enter' && canModify) {
                    await addNewItem(newItemValue);
                    setNewItemValue('');
                    if (newItemRef.current) {
                      newItemRef.current.focus();
                    }
                  }
                }}
                placeholder={canModify ? "add item..." : "read-only"}
                variant="standard"
                fullWidth
                disabled={!canModify || isCheckingPermissions}
                sx={{
                  fontFamily: theme.typography.fontFamily,
                  '& .MuiInputBase-input': {
                    padding: '4px 0',
                    fontSize: '0.85rem',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    fontSize: '0.85rem',
                    opacity: 0.4,
                  },
                  '& .MuiInput-underline:before': {
                    borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                  },
                  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                    borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                  },
                  '& .MuiInput-underline:after': {
                    borderColor: accent,
                  },
                }}
                inputRef={newItemRef}
              />
            </Box>
            
            {/* Regenerate button */}
            {onRegenerate && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pt: 1.5,
                }}
              >
                <Button
                  size="small"
                  onClick={onRegenerate}
                  disabled={isRegenerating}
                  startIcon={isRegenerating ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "0.75rem",
                    color: theme.palette.text.disabled,
                    textTransform: "lowercase",
                    py: 0.5,
                    px: 1,
                    borderRadius: "6px",
                    "&:hover": {
                      backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                      color: theme.palette.text.secondary,
                    },
                  }}
                >
                  {isRegenerating ? "..." : "regenerate"}
                </Button>
              </Box>
            )}
          </Box>
        )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={closeSnackbar}
          severity="success"
          variant="filled"
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={undoDelete}
              sx={{
                fontWeight: 600,
                fontSize: '0.8rem',
                letterSpacing: '0.02em',
              }}
            >
              undo
            </Button>
          }
          sx={{
            alignItems: 'center',
            borderRadius: '12px',
            fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
            fontSize: '0.9rem',
            backdropFilter: 'blur(12px)',
          }}
        >
          item removed
        </Alert>
      </Snackbar>
    </Box>
  );
}
